import Intercom from '@intercom/messenger-js-sdk';
import { IntercomUser, IntercomSettings } from '../types/intercom';

/**
 * Intercom Service
 * Handles all Intercom-related functionality
 */
class IntercomService {
  private static instance: IntercomService;
  private isInitialized: boolean = false;
  private appId: string = process.env.REACT_APP_INTERCOM_APP_ID || ""; 
  private currentUser: IntercomUser | null = null;

  private constructor() {}

  static getInstance(): IntercomService {
    if (!IntercomService.instance) {
      IntercomService.instance = new IntercomService();
    }
    return IntercomService.instance;
  }

  /**
   * Initialize Intercom with user data
   */
  boot(user: IntercomUser): void {
    try {
      if (this.isInitialized) {
        console.log('Intercom already initialized, updating user...');
        this.update(user);
        return;
      }

      const settings: IntercomSettings = {
        app_id: this.appId,
        user_id: user.user_id,
        name: user.name,
        email: user.email,
        created_at: user.created_at,
        role: user.role,
      };

      // Initialize Intercom
      Intercom(settings);

      this.isInitialized = true;
      this.currentUser = user;
      
      // Hide messenger by default - will show only on icon click
      this.hide();

      console.log('Intercom initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Intercom:', error);
    }
  }

  /**
   * Update user information
   */
  update(user: IntercomUser): void {
    try {
      if (!this.isInitialized) {
        console.warn('Intercom not initialized. Call boot() first.');
        return;
      }

      if (window.Intercom) {
        window.Intercom('update', {
          user_id: user.user_id,
          name: user.name,
          email: user.email,
          created_at: user.created_at,
          role: user.role,
        });

        this.currentUser = user;
        console.log('Intercom user updated');
      }
    } catch (error) {
      console.error('Failed to update Intercom:', error);
    }
  }

  /**
   * Show Intercom messenger
   */
  show(): void {
    try {
      if (!this.isInitialized) {
        console.warn('Intercom not initialized. Cannot show messenger.');
        return;
      }

      if (window.Intercom) {
        window.Intercom('show');
      }
    } catch (error) {
      console.error('Failed to show Intercom:', error);
    }
  }

  /**
   * Hide Intercom messenger
   */
  hide(): void {
    try {
      if (window.Intercom) {
        window.Intercom('hide');
      }
    } catch (error) {
      console.error('Failed to hide Intercom:', error);
    }
  }

  /**
   * Shutdown Intercom (on logout)
   */
  shutdown(): void {
    try {
      if (window.Intercom) {
        window.Intercom('shutdown');
        this.isInitialized = false;
        this.currentUser = null;
        console.log('Intercom shutdown successfully');
      }
    } catch (error) {
      console.error('Failed to shutdown Intercom:', error);
    }
  }

  /**
   * Check if Intercom is initialized
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Get current user
   */
  getCurrentUser(): IntercomUser | null {
    return this.currentUser;
  }
}

export default IntercomService.getInstance();
